import React from "react";
import { motion } from "framer-motion";
import { MapPin, Compass, ArrowRight } from "lucide-react";
import { AREA_LOCATIONS, IMAGES } from "../../data";

export const ServiceArea = () => {
  return (
    <section id="area" data-testid="area-section" className="bg-[#EAF7FC] py-16 md:py-24 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="rounded-[2rem] overflow-hidden shadow-[0_24px_64px_rgba(19,57,85,0.18)] aspect-[4/3]">
            <img src={IMAGES.charleston} alt="Charleston Lowcountry home" loading="lazy" className="h-full w-full object-cover" />
          </div>
          <div className="absolute -top-5 -right-4 bg-[#133955] text-white rounded-2xl px-6 py-5 shadow-xl">
            <Compass size={22} className="text-[#54BCE6] mb-2" />
            <p className="font-display text-lg font-bold leading-tight">South Carolina<br />Lowcountry</p>
          </div>
        </motion.div>

        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5 }}
            className="text-xs uppercase tracking-[0.24em] font-bold text-[#54BCE6] mb-4"
          >
            Where we connect
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
            className="font-display text-[#133955] font-extrabold tracking-tighter text-4xl md:text-5xl leading-[0.98]"
          >
            Connecting Homeowners Across the Lowcountry.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="mt-5 text-base md:text-lg text-[#173042]/75 max-w-lg"
          >
            Home Pro Connect helps homeowners in Charleston, Summerville, and surrounding
            communities begin the search for contractors serving their area.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.14 }}
            className="mt-4 text-base text-[#173042]/70 max-w-lg"
          >
            Home Pro Connect helps homeowners across our South Carolina service area begin the
            process of finding contractors for additions, complete remodels, demolition,
            structural changes, kitchen and bathroom renovations, and other major residential projects.
          </motion.p>

          <div className="mt-8 flex flex-wrap gap-3">
            {AREA_LOCATIONS.map((city, i) => (
              <motion.span
                key={city}
                initial={{ opacity: 0, scale: 0.92 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                data-testid={`area-chip-${i}`}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold shadow-[0_4px_16px_rgba(19,57,85,0.05)] ${
                  city.startsWith("Surrounding")
                    ? "bg-[#133955] text-white"
                    : "bg-white border border-[rgba(19,57,85,0.08)] text-[#133955]"
                }`}
              >
                {!city.startsWith("Surrounding") && <MapPin size={15} className="text-[#54BCE6]" />}
                {city}
              </motion.span>
            ))}
          </div>

          <a
            href="#request"
            data-testid="area-cta"
            className="group mt-9 inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-8 py-4 font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#133955] focus-visible:ring-offset-2"
          >
            Check Your Project Location
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </section>
  );
};
